import { useMemo, useState } from 'react'
import { useBilling } from '../contexts/BillingContext'
import { BillingNavigator } from '../types/billing'
import { ReportSection } from '../pages/billing/reports/ReportTable'
import { collections, salesTransactions, summariseSales, taxSummary } from '../utils/billingReports'
import { formatBillingDate, fromN, monthRange, todayLusaka, weekRange } from '../utils/billingMoney'

export type ReportKind = 'daily' | 'weekly' | 'monthly' | 'tax'
export const reportTitles: Record<ReportKind, string> = { daily: 'Daily Sales Report', weekly: 'Weekly Sales Report', monthly: 'Monthly Sales Report', tax: 'Monthly Sales Tax Report' }
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']

/** Builds the selected report from saved transactions. All sections share the same filters. */
export function useBillingReport(nav: BillingNavigator) {
  const { documents, sales, creditNotes, refunds, payments, config, items, accounts } = useBilling()
  const today = todayLusaka()
  const [kind, setKind] = useState<ReportKind>(config.regional.defaultReport ?? 'daily')
  const [day, setDay] = useState(today)
  const [year, setYear] = useState(Number(today.slice(0, 4)))
  const [month, setMonth] = useState(Number(today.slice(5, 7)))
  const [department, setDepartment] = useState('')
  const [itemId, setItemId] = useState('')
  const [accountId, setAccountId] = useState('')
  const [cashier, setCashier] = useState('')
  const date = (d: string) => formatBillingDate(d, config.regional.dateFormat)

  const range = kind === 'daily' ? { from: day, to: day } : kind === 'weekly' ? weekRange(day, config.regional.weekStart) : monthRange(year, month)
  const period = kind === 'daily' ? date(day) : kind === 'weekly' ? `Week ${date(range.from)} – ${date(range.to)} (weeks start ${config.regional.weekStart ?? 'Monday'})` : `${MONTHS[month - 1]} ${year}`

  const src = { documents, sales, creditNotes, refunds, payments }
  const filters = { ...range, department, itemId, accountId, cashier }
  const txns = useMemo(() => salesTransactions(src, filters),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [documents, sales, creditNotes, range.from, range.to, department, itemId, accountId, cashier])
  const summary = useMemo(() => summariseSales(txns), [txns])
  const cash = useMemo(() => collections(src, filters),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [payments, sales, refunds, range.from, range.to, accountId, cashier])
  const tax = useMemo(() => taxSummary(txns), [txns])

  // Filter options come from the configured lists plus anything found on saved transactions.
  const options = useMemo(() => {
    const all = salesTransactions(src, { from: '0000-01-01', to: '9999-12-31', department: '', itemId: '', accountId: '', cashier: '' })
    return {
      departments: [...new Set([...config.catalogue.departments, ...all.flatMap((t) => t.lines.map((l) => l.department))])].sort(),
      cashiers: [...new Set([...all.map((t) => t.cashier), ...payments.map((p) => p.receivedByName)])].filter(Boolean).sort(),
      accounts: accounts.filter((a) => all.some((t) => t.accountId === a.id)).sort((a, b) => a.name.localeCompare(b.name)),
      items: [...items].sort((a, b) => a.name.localeCompare(b.name)),
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documents, sales, creditNotes, payments, accounts, items, config.catalogue.departments])

  const open = (i: number, list = txns) => {
    const t = list[i]
    if (!t) return
    if (t.kind === 'Direct sale' && sales.some((s) => s.id === t.id)) nav.openDirectSale(t.id)
    else nav.openDocument(t.sourceId)
  }

  const basis = 'Basis: accrual — invoices count on their invoice date, direct sales on their receipt date, credit notes on their credit-note date. Period boundaries in Africa/Lusaka time. Drafts, voided transactions, quotations and orders are excluded. Payments on invoices are collections, not new sales.'
  const filterText = [
    department && `Department ${department}`,
    itemId && `Item ${items.find((i) => i.id === itemId)?.name ?? itemId}`,
    accountId && `Payer ${accounts.find((a) => a.id === accountId)?.name ?? accountId}`,
    cashier && `Cashier ${cashier}`,
  ].filter(Boolean).join(' · ')

  const k = (n: number) => fromN(n)
  const salesCols = [
    { key: 'label', label: '' }, { key: 'count', label: 'Count', align: 'right' as const }, { key: 'gross', label: 'Gross', money: true },
    { key: 'discount', label: 'Discounts', money: true }, { key: 'net', label: 'Net excl. tax', money: true }, { key: 'tax', label: 'Tax', money: true }, { key: 'total', label: 'Total incl. tax', money: true },
  ]
  const row = (label: string, t: typeof summary.invoice, sign = 1) => ({ label, count: t.count, gross: sign * t.gross, discount: sign * t.discount, net: sign * t.net, tax: sign * t.tax, total: sign * t.total })

  const salesSections: ReportSection[] = [
    {
      title: 'Sales summary',
      columns: salesCols,
      rows: [
        row('Invoice sales', summary.invoice),
        row('Direct sales (cash receipts)', summary.direct),
        { label: 'Total sales', count: summary.net.count, gross: summary.net.gross, discount: summary.net.discount, net: fromN(Math.round(summary.invoice.net * 100 + summary.direct.net * 100)), tax: fromN(Math.round(summary.invoice.tax * 100 + summary.direct.tax * 100)), total: fromN(Math.round(summary.invoice.total * 100 + summary.direct.total * 100)) },
        row('Less returns & credit notes', summary.credits, -1),
        { label: 'Net sales after adjustments', count: '', gross: '', discount: '', net: summary.net.net, tax: summary.net.tax, total: summary.net.total },
      ],
      emphasis: [2, 4],
    },
    {
      title: 'By department / service',
      note: 'Credit notes are spread across the departments of the invoice they correct.',
      columns: [{ key: 'name', label: 'Department' }, { key: 'net', label: 'Net excl. tax', money: true }, { key: 'tax', label: 'Tax', money: true }, { key: 'total', label: 'Total incl. tax', money: true }],
      rows: summary.departments,
    },
    {
      title: 'Top items & services',
      columns: [{ key: 'name', label: 'Item / service' }, { key: 'quantity', label: 'Qty', align: 'right' }, { key: 'net', label: 'Net excl. tax', money: true }, { key: 'total', label: 'Total incl. tax', money: true }],
      rows: summary.items.slice(0, 15),
    },
    {
      title: 'Money received (collections)',
      note: 'Cash collected is not the same as sales: it includes payments on earlier invoices and deposits, and excludes unpaid invoices. Department and item filters do not apply here.',
      columns: [{ key: 'method', label: 'Payment method' }, { key: 'amount', label: 'Amount', money: true }],
      rows: cash.byMethod,
      footer: { method: 'Net collected (after refunds)', amount: cash.total },
    },
    {
      title: 'Transactions',
      note: 'Select a row to open the underlying document.',
      columns: [{ key: 'date', label: 'Date' }, { key: 'number', label: 'Number' }, { key: 'kind', label: 'Type' }, { key: 'account', label: 'Customer / payer' }, { key: 'cashier', label: 'Staff' }, { key: 'net', label: 'Net excl. tax', money: true }, { key: 'tax', label: 'Tax', money: true }, { key: 'total', label: 'Total', money: true }],
      rows: txns.map((t) => { const s = t.kind === 'Credit note' ? -1 : 1; return { date: date(t.date), number: t.number, kind: t.kind, account: t.account, cashier: t.cashier, net: s * k(t.net), tax: s * k(t.tax), total: s * k(t.total) } }),
      onRow: (i) => open(i),
    },
  ]

  const taxRows = (rows: typeof tax.sales, sign = 1) => rows.map((g) => ({ code: g.taxCode, name: g.taxName, rate: `${g.taxRate}%`, cls: g.taxClass, count: g.count, taxable: sign * g.taxable, tax: sign * g.tax, gross: sign * g.gross }))
  const taxCols = [{ key: 'code', label: 'Code' }, { key: 'name', label: 'Tax' }, { key: 'rate', label: 'Rate', align: 'right' as const }, { key: 'cls', label: 'Class' }, { key: 'count', label: 'Docs', align: 'right' as const }, { key: 'taxable', label: 'Taxable amount', money: true }, { key: 'tax', label: 'Tax charged', money: true }, { key: 'gross', label: 'Total incl. tax', money: true }]
  const periodRefunds = refunds.filter((r) => r.date >= range.from && r.date <= range.to && (!accountId || r.accountId === accountId) && (!cashier || r.createdByName === cashier))
  const taxSections: ReportSection[] = [
    {
      title: 'Summary by classification',
      columns: [{ key: 'cls', label: 'Classification' }, { key: 'sales', label: 'Sales (net)', money: true }, { key: 'salesTax', label: 'Tax on sales', money: true }, { key: 'credits', label: 'Credit adjustments', money: true }, { key: 'creditTax', label: 'Tax on credits', money: true }, { key: 'net', label: 'Net taxable', money: true }, { key: 'netTax', label: 'Net tax', money: true }],
      rows: tax.classes.map((c) => ({ ...c, credits: -c.credits, creditTax: -c.creditTax })),
      footer: { cls: 'Net tax for the month', sales: '', salesTax: '', credits: '', creditTax: '', net: '', netTax: tax.netTax },
    },
    { title: 'Sales by tax code and rate', note: 'Invoices and direct sales, using the tax code and rate saved on each line when it was issued.', columns: taxCols, rows: taxRows(tax.sales) },
    { title: 'Credit-note adjustments', note: 'Credit notes issued this month reduce taxable sales in proportion to the invoice lines they correct.', columns: taxCols, rows: taxRows(tax.credits, -1) },
    {
      title: 'Refunds (no tax effect)',
      note: 'Refunds return unapplied money (overpayments, deposits or credit already recorded above). They do not change taxable sales again.',
      columns: [{ key: 'date', label: 'Date' }, { key: 'number', label: 'Refund' }, { key: 'account', label: 'Account' }, { key: 'source', label: 'From' }, { key: 'amount', label: 'Amount', money: true }],
      rows: periodRefunds.map((r) => ({ date: date(r.date), number: r.number, account: r.accountName, source: r.sourceNumber, amount: r.amount })),
      footer: periodRefunds.length ? { date: 'Total refunded', number: '', account: '', source: '', amount: fromN(periodRefunds.reduce((s, r) => s + Math.round(r.amount * 100), 0)) } : undefined,
    },
    salesSections[4],
  ]

  return {
    kind, setKind, day, setDay, year, setYear, month, setMonth, months: MONTHS,
    department, setDepartment, itemId, setItemId, accountId, setAccountId, cashier, setCashier, options,
    range, period, basis, filterText, txns, cash, summary,
    sections: kind === 'tax' ? taxSections : salesSections,
    isEmpty: txns.length === 0 && cash.rows.length === 0 && periodRefunds.length === 0,
  }
}
